import Head from 'next/head'
import Link from 'next/link'
import { ReactNode, useEffect } from 'react'
import { PiArrowLeftBold } from 'react-icons/pi'

import { useMenuState } from 'src/providers/MenuStateProvider'
import { useUser } from 'src/providers/UserProvider'

import {
  BASE_CLASSES,
  VARIANT_CLASSES,
  VARIANT_HOVER_CLASSES
} from 'src/components/atoms/ButtonRebrand/styles'
import { Image } from 'src/components/atoms/Image'
import { InternalLink } from 'src/components/atoms/Link'
import { LogoRebrand } from 'src/components/atoms/LogoRebrand'
import { Loader } from 'src/components/layouts/Loader'
import { AccountDropdownNew } from 'src/components/organisms/AccountDropdown/AccountDropdownNew'
import { ConsentBanner } from 'src/components/organisms/ConsentBanner'
import { Modal } from 'src/components/templates/Modal'

import { donatePath, homePath } from 'src/config/paths'
import useMediaQuery from 'src/hooks/useMediaQuery'
import { useMessenger } from 'src/hooks/useMessenger'
import { NavigationContext } from 'src/types/account'
import { trackEvent } from 'src/utils/ga'

import { Breadcrumbs } from '../../Breadcrumbs'
import { FooterRebrandMinimal } from '../../Footer'
import { MobileNavigation } from '../../Header/MobileNavigation'
import { MobileNavigationToggle } from '../../Header/MobileNavigationToggle'
import { buildDashboardFunderLinks } from '../../Header/helper'
import { DEFAULT_SEO_DESCRIPTION } from '../Page'
import { DashboardMenu } from './DashboardMenu'

export type PageProps = {
  children: ReactNode | ReactNode[]
  showHero?: boolean
  title: string
  isIntercomEnabled?: boolean
  metaTitle?: string
  metaDescription?: string
  showBreadcrumbs?: boolean
}

export const Page = ({
  children,
  showHero,
  title,
  isIntercomEnabled = false,
  metaTitle,
  metaDescription = DEFAULT_SEO_DESCRIPTION,
  showBreadcrumbs = true
}: PageProps) => {
  const { boot, shutdown } = useMessenger()

  useEffect(() => {
    if (isIntercomEnabled) {
      boot()
    }
    return () => {
      shutdown()
    }
  }, [boot, isIntercomEnabled, shutdown])

  const { user } = useUser()

  const DEFAULT_PROFILE = 'assets/img/default-profile.png'

  const onDonateClick = () => {
    trackEvent(
      user?.Account?.BusinessType
        ? 'business_donate_button'
        : 'non_business_donate_button',
      {}
    )
  }
  const isMobile = useMediaQuery('(max-width: 1024px)')

  const { isOpen, setIsOpen } = useMenuState()
  useEffect(() => {
    if (!isMobile) {
      setIsOpen(false)
    }
  }, [isMobile, setIsOpen])

  return (
    <>
      <Head key='dashboardHead'>
        <title>{`${title} | Trees That Count`}</title>
        <meta property='og:title' content={`${metaTitle} | Trees That Count`} />
        <meta property='og:description' content={metaDescription} />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
      </Head>
      <main className='w-full min-h-[80vh] relative bg-linen'>
        <Loader>
          <div className='mx-auto text-shark'>
            <div className='flex'>
              {!isMobile && (
                <div className='bg-forestDeep w-2/12 min-w-67.5 flex flex-col min-h-[calc(100vh-10px)]'>
                  <div className='pt-8 pl-4 pb-12 bg-forestDeepDarker'>
                    <InternalLink
                      href={homePath}
                      className='text-white hover:text-white relative h-15 w-37.5 block'
                    >
                      <LogoRebrand />
                    </InternalLink>
                  </div>
                  <div className='flex items-start px-6 pb-4'>
                    <PiArrowLeftBold className='mr-2 mt-1 shrink-0 text-white' />
                    <InternalLink
                      color='none'
                      className='font-normal! text-white underline hover:no-underline'
                      href='/'
                    >
                      Return to the website
                    </InternalLink>
                  </div>
                  <DashboardMenu isBusiness={!!user?.Account?.BusinessType} />
                </div>
              )}
              <div className={`${isMobile ? 'w-full' : 'w-10/12'}`}>
                <div className='relative w-full'>
                  <div
                    className={`w-full relative ${
                      showHero ? 'min-h-60 ' : 'min-h-35'
                    }`}
                  >
                    <Image
                      key='dashboardImage'
                      alt={`Banner for ${user?.Account?.Name}`}
                      src='/img/dashboardBackground.webp'
                      fill
                      className={`object-cover object-[0%_0%] ${
                        !showHero ? 'lg:object-[0%_30%]' : 'lg:object-[0%_50%]'
                      }`}
                    />
                  </div>
                </div>
                <div
                  className={`flex items-start lg:items-center justify-end gap-3 absolute top-5 right-5 ${
                    isMobile && 'left-0 right-0! pr-3 [&>div:first-child]:mx-0'
                  }`}
                >
                  {isMobile && (
                    <MobileNavigationToggle
                      navigationContext={NavigationContext.Funder}
                      showReturnLink
                    />
                  )}
                  {isOpen ? (
                    <div
                      role='presentation'
                      onClick={() => setIsOpen(false)}
                      onKeyDown={e => e.key === 'Escape' && setIsOpen(false)}
                      className='menu-overlay z-10'
                    >
                      <MobileNavigation
                        links={buildDashboardFunderLinks(
                          !!user?.Account?.BusinessType
                        )}
                        navigationContext={NavigationContext.Funder}
                      />
                    </div>
                  ) : null}
                  {!isMobile && (
                    <AccountDropdownNew
                      accountName={user?.Account?.Name}
                      avatarPath={
                        user?.Account?.Avatar &&
                        user?.Account?.Avatar !== DEFAULT_PROFILE
                          ? user.Account.Avatar
                          : undefined
                      }
                    />
                  )}
                  <Link
                    href={donatePath()}
                    onClick={onDonateClick}
                    className={`${BASE_CLASSES} ${VARIANT_CLASSES.lavender} ${VARIANT_HOVER_CLASSES.lavender} shrink-0 px-5! lg:px-8!`}
                  >
                    Donate
                  </Link>
                </div>
                <div
                  className={`${
                    showHero ? '-mt-45 lg:-mt-67.5' : 'mt-7.5 xl:mt-10'
                  } relative mx-10 xl:mx-20`}
                >
                  {showBreadcrumbs && (
                    <Breadcrumbs className='w-full! -ml-2! px-0! max-w-full!' />
                  )}
                  <div className={`${!showHero && 'my-4'}`}>{children}</div>
                </div>
              </div>
            </div>
          </div>
        </Loader>
      </main>
      <FooterRebrandMinimal />
      <Modal />
      <ConsentBanner />
    </>
  )
}
